import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Card } from '@magnetic/card'
import { Button } from '@magnetic/button'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Notification } from '@magnetic/notification'
import { Tag } from '@magnetic/tag'
import { Text } from '@magnetic/text'
import { Tooltip } from '@magnetic/tooltip'
import { Spinner } from '@magnetic/spinner'
import {
  ClockCounterClockwise,
  Calendar,
  User,
  Package,
  Clock,
  WarningCircle,
  ArrowsClockwise,
  ArrowRight,
  // Magnetic Status Icons
  Positive,
  Negative,
  InProgress,
} from '@/lib/icons'
import { getImageDeployments } from '@/lib/api'
import { formatTimestamp } from '@/lib/protobuf-utils'

interface DeploymentHistoryProps {
  applicationId: string
}

export function DeploymentHistory({ applicationId }: DeploymentHistoryProps) {
  const { data: deploymentsResponse, isLoading, error, refetch } = useConnectQuery(
    getImageDeployments,
    { applicationId },
    {
      refetchInterval: 30000,
    }
  )

  const deployments = deploymentsResponse?.deployments

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Positive />
      case 'failed':
        return <Negative />
      case 'in_progress':
        return <InProgress />
      default:
        return <Clock size={16} style={{ opacity: 0.5 }} />
    }
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { status: 'positive' | 'warning' | 'negative' | 'inactive'; label: string }> = {
      completed: { status: 'positive', label: 'Completed' },
      success: { status: 'positive', label: 'Success' },
      failed: { status: 'negative', label: 'Failed' },
      in_progress: { status: 'warning', label: 'In Progress' },
      syncing: { status: 'warning', label: 'Syncing' },
      pending: { status: 'inactive', label: 'Pending' },
    }
    const variant = variants[status] || { status: 'inactive' as const, label: status }
    return <Tag.Status status={variant.status}>{variant.label}</Tag.Status>
  }

  if (isLoading) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <ClockCounterClockwise size={20} />
            <Heading size="sub-section">Deployment History</Heading>
          </Flex>
          <Flex align="center" justify="center" style={{ padding: 32 }}>
            <Spinner size="md" />
          </Flex>
        </Flex>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <ClockCounterClockwise size={20} />
            <Heading size="sub-section">Deployment History</Heading>
          </Flex>
          <Flex direction="vertical" align="center" gap="md" style={{ padding: 32 }}>
            <WarningCircle size={32} style={{ color: 'var(--mds-color-theme-text-error-normal)' }} />
            <Text size="sm" color="light">
              {error instanceof Error ? error.message : 'Failed to load deployment history'}
            </Text>
            <Button kind="secondary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />}>
              Retry
            </Button>
          </Flex>
        </Flex>
      </Card>
    )
  }

  if (!deployments || deployments.length === 0) {
    return (
      <Card>
        <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
          <Flex align="center" gap="sm">
            <ClockCounterClockwise size={20} />
            <Heading size="sub-section">Deployment History</Heading>
          </Flex>
          <Flex direction="vertical" align="center" style={{ padding: 32, opacity: 0.5 }}>
            <ClockCounterClockwise size={32} />
            <Text size="sm">No deployment history yet</Text>
          </Flex>
        </Flex>
      </Card>
    )
  }

  return (
    <Card>
      <Flex direction="vertical" gap="md" style={{ padding: 16 }}>
        <Flex align="center" justify="space-between">
          <Flex align="center" gap="sm">
            <ClockCounterClockwise size={20} />
            <Heading size="sub-section">Deployment History</Heading>
          </Flex>
          <Tooltip title="Refresh history">
            <Button kind="tertiary" size="sm" onClick={() => refetch()} icon={<ArrowsClockwise size={16} />} />
          </Tooltip>
        </Flex>

        <Flex direction="vertical" gap="md">
          {deployments.map((deployment) => (
            <Flex
              key={deployment.id}
              gap="md"
              style={{
                padding: 16,
                borderRadius: 8,
                border: '1px solid var(--mds-color-theme-outline-secondary-normal)',
              }}
            >
              <Flex style={{ marginTop: 2 }}>{getStatusIcon(deployment.status)}</Flex>

              <Flex direction="vertical" gap="sm" style={{ flex: 1, minWidth: 0 }}>
                <Flex align="center" gap="sm">
                  {getStatusBadge(deployment.status)}
                  {deployment.completedAt && (
                    <Text size="xs" color="light">
                      {formatTimestamp(deployment.deployedAt)}
                    </Text>
                  )}
                </Flex>

                <Flex direction="vertical" gap="sm">
                  <Flex align="center" gap="sm">
                    <Package size={16} style={{ opacity: 0.7 }} />
                    <Text size="sm" color="light">Image:</Text>
                    <Flex align="center" gap="xs">
                      <Tag>{deployment.sourceImage?.split('/').pop()?.split(':')[0] || 'unknown'}</Tag>
                      <ArrowRight size={12} style={{ opacity: 0.5 }} />
                      <Tag>{deployment.sourceImage?.split(':').pop() || 'latest'}</Tag>
                    </Flex>
                  </Flex>

                  {deployment.imageDigest && (
                    <Flex align="center" gap="sm">
                      <Text size="sm" color="light">Digest:</Text>
                      <Text size="xs" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {deployment.imageDigest}
                      </Text>
                    </Flex>
                  )}

                  <Flex align="center" gap="md" style={{ opacity: 0.7 }}>
                    <Flex align="center" gap="xs">
                      <User size={16} />
                      <Text size="sm">{deployment.deployedBy || 'System'}</Text>
                    </Flex>
                    <Flex align="center" gap="xs">
                      <Calendar size={16} />
                      <Text size="sm">{formatTimestamp(deployment.deployedAt)}</Text>
                    </Flex>
                  </Flex>

                  {deployment.statusMessage && deployment.status === 'failed' && (
                    <Notification status="negative" title="Deployment failed">
                      <Text size="sm">{deployment.statusMessage}</Text>
                    </Notification>
                  )}
                </Flex>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Card>
  )
}
